import {
  ColumnFiltersState,
  flexRender,
  OnChangeFn,
  RowData,
  SortingState,
} from "@tanstack/react-table";
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  LegacyColumnDef,
  LegacyRow,
  LegacyTable,
  useLegacyTable,
} from "@tanstack/react-table/legacy";
import React, { useCallback, useState } from "react";
import { TractieIcon } from "@tractie/components/react/TractieIcon";
import { LoadingState } from "@shared/common/components/State/LoadingState";
import { DataTableRow } from "./DataTableRow";

declare module "@tanstack/react-table" {
  interface ColumnMeta<TFeatures, TData extends RowData, TValue> {
    className?: string;
  }
}

export type DataTableColumnDef<T extends RowData> = LegacyColumnDef<T>;
export type DataTableRow<T extends RowData> = LegacyRow<T>;

interface DataTableOptions<T extends RowData> {
  readonly loadingMessage?: string;
  readonly emptyMessage?: string;
  readonly rowClick?: (row: DataTableRow<T>) => void;
  readonly isRowInteractive?: (row: DataTableRow<T>) => boolean;
  readonly getRowId?: (originalRow: T, index: number, parent?: DataTableRow<T>) => string;
}

interface DataTableProps<T extends RowData> {
  readonly columns: DataTableColumnDef<T>[];
  readonly data: T[];
  readonly isLoading?: boolean;
  readonly options?: DataTableOptions<T>;
  readonly defaultSort?: SortingState;
  readonly filters?: ColumnFiltersState;
  readonly enableExpanding?: boolean;
  readonly getRowCanExpand?: (row: DataTableRow<T>) => boolean;
  readonly getSubTable?: (row: DataTableRow<T>) => React.ReactNode;
  readonly enableSorting?: boolean;
  readonly className?: string;
  readonly onSortingChange?: (sorting: SortingState) => void;
  readonly "data-testid"?: string;
}

const emptyFilters: ColumnFiltersState = [];

const ariaSortMap = {
  asc: "ascending",
  desc: "descending",
  none: "none",
} as const;

const sortIconMap = {
  asc: "sort-down",
  desc: "sort-up",
  none: "sort",
} as const;

export function DataTable<T extends RowData>({
  columns,
  data,
  options = {},
  defaultSort,
  filters = emptyFilters,
  enableExpanding = false,
  getRowCanExpand,
  getSubTable,
  enableSorting = true,
  isLoading = false,
  className,
  onSortingChange,
  "data-testid": tableTestId,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(defaultSort || []);

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newSorting =
      typeof updaterOrValue === "function" ? updaterOrValue(sorting) : updaterOrValue;
    setSorting(newSorting);
    onSortingChange?.(newSorting);
  };

  const table = useLegacyTable<T>({
    onSortingChange: handleSortingChange,
    enableMultiSort: false,
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: options.getRowId,
    state: { sorting, columnFilters: filters },
    enableExpanding,
    getRowCanExpand,
    enableSorting,
  });

  const isRowInteractive = useCallback(
    (row: DataTableRow<T>) => {
      const isRowInteractive =
        options.isRowInteractive ?? ((r: DataTableRow<T>) => r.getCanExpand());
      return isRowInteractive ? isRowInteractive(row) : false;
    },
    [options],
  );

  return (
    <table data-testid={tableTestId} className={className}>
      <DataTableHeader table={table} />
      <DataTableBody
        table={table}
        options={options}
        isLoading={isLoading}
        isRowInteractive={isRowInteractive}
        getSubTable={getSubTable}
      />
    </table>
  );
}

function DataTableHeader<T extends RowData>({ table }: { readonly table: LegacyTable<T> }) {
  return (
    <thead className="sticky top-0 z-1">
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const canSort = header.column.getCanSort();
            const sortDirection = header.column.getIsSorted() || "none";
            return (
              <th
                key={header.id}
                colSpan={header.colSpan}
                aria-sort={canSort ? ariaSortMap[sortDirection] : undefined}
                onClick={header.column.getToggleSortingHandler()}
                style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                className={header.column.columnDef.meta?.className}
              >
                {header.isPlaceholder ? null : (
                  <>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {canSort && (
                      <TractieIcon
                        name={sortIconMap[sortDirection]}
                        className={header.column.getIsSorted() ? "visible!" : undefined}
                        aria-hidden="true"
                      />
                    )}
                  </>
                )}
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}

interface DataTableBodyProps<T extends RowData> {
  readonly table: LegacyTable<T>;
  readonly options: DataTableOptions<T>;
  readonly isLoading: boolean;
  readonly isRowInteractive: (row: DataTableRow<T>) => boolean;
  readonly getSubTable?: (row: DataTableRow<T>) => React.ReactNode;
}

function DataTableBody<T extends RowData>({
  table,
  options,
  isLoading,
  isRowInteractive,
  getSubTable,
}: DataTableBodyProps<T>) {
  return (
    <tbody>
      {table.getRowModel().rows.length > 0 ? (
        table.getRowModel().rows.map((row) => {
          return (
            <React.Fragment key={row.id}>
              <DataTableRow
                row={row}
                isRowInteractive={isRowInteractive}
                onClick={() => options.rowClick && options.rowClick(row)}
                {...(row.getCanExpand() && {
                  onClick: row.getToggleExpandedHandler(),
                })}
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td
                      key={cell.id}
                      className={`align-middle ${cell.column.columnDef.meta?.className ?? ""}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </DataTableRow>
              {row.getIsExpanded() && <tr key={row.id + "expansion"}>{getSubTable?.(row)}</tr>}
            </React.Fragment>
          );
        })
      ) : (
        <DataTableEmptyState
          columnCount={table.getAllLeafColumns().length}
          isLoading={isLoading}
          loadingMessage={options.loadingMessage}
          emptyMessage={options.emptyMessage}
        />
      )}
    </tbody>
  );
}

interface DataTableEmptyStateProps {
  readonly columnCount: number;
  readonly isLoading: boolean;
  readonly loadingMessage?: string;
  readonly emptyMessage?: string;
}

function DataTableEmptyState({
  columnCount,
  isLoading,
  loadingMessage,
  emptyMessage,
}: DataTableEmptyStateProps) {
  return (
    <tr>
      <td colSpan={columnCount} data-empty-state="true">
        {isLoading ? <LoadingState message={loadingMessage} fullPage={false} /> : emptyMessage}
      </td>
    </tr>
  );
}
